import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ShieldCheck, Lock, MessageCircle, Mail, Phone, AlertTriangle, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { usePageMeta, useNoIndex } from "@/lib/seo";
import { PaymentMethodIcons } from "@/components/PaymentMethods";

export default function PayPage() {
  usePageMeta("Secure Payment", "Pay securely by card for your Makchester Tours & Travel booking.");
  useNoIndex();
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState(null);

  useEffect(() => {
    api.get(`/payments/pay/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "This payment link could not be loaded."));
  }, [token]);

  const payNow = async () => {
    setPaying(true);
    setPayError(null);
    try {
      const { data: res } = await api.post("/payments/checkout", { token });
      window.location.href = res.redirect_url;
    } catch (e) {
      setPayError(e.response?.data?.detail || "Payment could not be started. Please try again.");
      setPaying(false);
    }
  };

  if (error) return <Shell><StatusCard icon={AlertTriangle} tone="amber" title="Link unavailable" text={error} /></Shell>;
  if (!data) return <Shell><div className="text-sm text-slate-300 text-center py-20">Loading secure payment details…</div></Shell>;

  const closed = data.status !== "open";
  return (
    <Shell>
      <div className="max-w-lg mx-auto bg-white rounded-2xl overflow-hidden shadow-2xl" data-testid="pay-page-card">
        <div className="bg-[#0A2E4D] px-8 py-6">
          <div className="text-[#E3BA45] text-[11px] font-bold uppercase tracking-[0.2em]">Secure Payment</div>
          <h1 className="font-serif-display text-2xl text-white mt-1">Makchester Tours &amp; Travel</h1>
          {data.test_mode && <div className="mt-2 inline-block bg-amber-400/20 text-amber-300 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full" data-testid="test-mode-badge">Test mode — no real charge</div>}
        </div>
        <div className="px-8 py-7">
          <dl className="space-y-2.5 text-sm">
            <Row label="Customer" value={data.customer_name} testid="pay-customer" />
            <Row label="Service" value={data.service} testid="pay-service" />
            <Row label="Booking reference" value={data.booking_reference} testid="pay-booking-ref" />
            <Row label="Payment reference" value={data.payment_ref} testid="pay-ref" />
            <div className="pt-3 mt-3 border-t border-dashed border-[#E5E7EB]" />
            <Row label="Total approved" value={`AED ${data.total_approved_aed.toLocaleString()}`} />
            <Row label="Already paid" value={`AED ${data.already_paid_aed.toLocaleString()}`} green />
            <Row label="Outstanding balance" value={`AED ${data.outstanding_aed.toLocaleString()}`} />
            {data.expires_at && data.status === "open" && (
              <Row label="Link valid until" value={new Date(data.expires_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })} />
            )}
          </dl>

          <div className="mt-6 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl p-5 text-center">
            <div className="text-[11px] uppercase tracking-wider font-bold text-[#4B5563]">Amount due now</div>
            <div className="font-serif-display text-4xl text-[#0A2E4D] mt-1" data-testid="pay-amount">AED {data.amount_aed.toLocaleString()}</div>
          </div>

          {data.status === "paid" ? (
            <div className="mt-6 flex items-center justify-center gap-2 text-emerald-600 font-semibold text-sm" data-testid="pay-already-paid"><CheckCircle2 size={17} /> This payment has been completed. Thank you!</div>
          ) : closed ? (
            <div className="mt-6 flex items-center justify-center gap-2 text-amber-600 font-semibold text-sm" data-testid="pay-link-closed"><AlertTriangle size={17} /> This payment link is {data.status}. Please contact us for a new link.</div>
          ) : (
            <>
              <button onClick={payNow} disabled={paying} data-testid="pay-securely-btn"
                className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#D4A62A] hover:bg-[#BA901D] disabled:opacity-60 text-white py-4 rounded-full text-sm font-bold uppercase tracking-wider transition-colors">
                <Lock size={15} /> {paying ? "Connecting to secure gateway…" : "Pay Securely by Card"}
              </button>
              {payError && <p className="text-xs text-red-500 text-center mt-3" data-testid="pay-error">{payError}</p>}
              <div className="mt-6 bg-[#F9FAFB] border border-[#EDEFF2] rounded-[18px] px-5 py-6 flex flex-col items-center gap-4" data-testid="pay-trust-section">
                <span className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-bold text-[#0A2E4D]">
                  <Lock size={13} className="text-[#D4A62A]" strokeWidth={2.2} /> Secure Payment
                </span>
                <PaymentMethodIcons className="justify-center" />
                <p className="text-[11px] font-semibold tracking-wide text-[#4B5563] text-center">SSL Secured <span className="mx-1.5 text-[#D1D5DB]">•</span> PCI-DSS Compliant <span className="mx-1.5 text-[#D1D5DB]">•</span> Protected Checkout</p>
              </div>
              <p className="text-[11px] text-[#9CA3AF] text-center mt-4 flex items-center justify-center gap-1.5"><ShieldCheck size={12} /> Card details are entered on a PCI-DSS compliant secure payment page. The amount is fixed and cannot be changed.</p>
            </>
          )}

          <div className="mt-7 pt-5 border-t border-[#E5E7EB] text-center text-xs text-[#4B5563]">
            <div className="font-semibold text-[#0A2E4D] mb-2">Questions about this payment?</div>
            <div className="flex flex-wrap justify-center gap-4">
              <a href={data.contact.whatsapp} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#0A2E4D] hover:text-[#BA901D]" data-testid="pay-contact-whatsapp"><MessageCircle size={12} /> WhatsApp</a>
              <a href={`tel:${data.contact.phone.replace(/ /g, "")}`} className="inline-flex items-center gap-1 text-[#0A2E4D] hover:text-[#BA901D]"><Phone size={12} /> {data.contact.phone}</a>
              <a href={`mailto:${data.contact.email}`} className="inline-flex items-center gap-1 text-[#0A2E4D] hover:text-[#BA901D]"><Mail size={12} /> {data.contact.email}</a>
            </div>
          </div>
        </div>
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return <div className="bg-[#0A2E4D] min-h-screen py-16 px-4">{children}</div>;
}

function Row({ label, value, green, testid }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-[#4B5563]">{label}</dt>
      <dd className={`font-semibold text-right ${green ? "text-emerald-600" : "text-[#0A2E4D]"}`} data-testid={testid}>{value}</dd>
    </div>
  );
}

function StatusCard({ icon: Icon, tone, title, text }) {
  return (
    <div className="max-w-md mx-auto bg-white rounded-2xl p-10 text-center shadow-2xl">
      <Icon size={38} className={`mx-auto ${tone === "amber" ? "text-amber-500" : "text-[#D4A62A]"}`} strokeWidth={1.4} />
      <h1 className="font-serif-display text-xl text-[#0A2E4D] mt-4">{title}</h1>
      <p className="text-sm text-[#4B5563] mt-2">{text}</p>
      <a href="https://wa.me/971559209503" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-[#25D366] text-white px-6 py-3 rounded-full text-sm font-semibold mt-6"><MessageCircle size={14} /> Contact us on WhatsApp</a>
    </div>
  );
}
